import { useCallback, useEffect, useState } from 'react';
import { apiService, WhitelistStatusDTO } from '@/services/api.service';
import { GameGroupBadge } from '@/components/ui/GameGroupBadge';
import { useActiveGameGroup } from '@/stores/gameGroups.store';
import { useNotificationsStore } from '@/stores/notifications.store';
import { useAuthStore } from '@/stores/auth.store';
import settings from '@/pages/SettingsPage.module.scss';
import styles from './Whitelists.module.scss';

interface NetworkWhitelistCardProps {
  /** Appelé avec l'état lu : la page masque les noms d'un groupe sans liste du réseau (lobby). */
  onStatus?: (status: WhitelistStatusDTO | null) => void;
}

/**
 * Liste blanche du réseau (/whitelistall du proxy) : allumée ou éteinte, pour le groupe actif
 * (NationsGlory : Nova, Iris) ou pour tout le réseau (Paladium). Même effet en jeu que la commande.
 */
export function NetworkWhitelistCard({ onStatus }: NetworkWhitelistCardProps) {
  const group = useActiveGameGroup();
  // Lecture seule (<projet>.whitelist:read) : l'état sans l'interrupteur.
  const writable = useAuthStore((s) => s.canWrite)('whitelist');
  const notify = useNotificationsStore.getState;
  const [status, setStatus] = useState<WhitelistStatusDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await apiService.getWhitelistStatus();
    if (result.data && typeof result.data.enabled === 'boolean') {
      setStatus(result.data);
      onStatus?.(result.data);
    } else {
      setStatus(null);
      onStatus?.(null);
      setError(result.error ?? 'Réponse inattendue');
    }
    setLoading(false);
  }, [onStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = async (enabled: boolean) => {
    setSaving(true);
    const result = await apiService.setWhitelistStatus(enabled);
    setSaving(false);
    setConfirming(null);
    if (result.data) {
      setStatus(result.data);
      onStatus?.(result.data);
      notify().success(enabled ? 'Liste blanche du réseau activée.' : 'Liste blanche du réseau désactivée.');
    } else {
      notify().error(result.error ?? 'Changement impossible.', 12000);
      load();
    }
  };

  const where = group ? group.label : 'tout le réseau';

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>
          Liste blanche du réseau <GameGroupBadge />
        </div>
        <span className={styles.sectionHint}>Commande en jeu : /whitelistall</span>
      </div>

      <div className={styles.card}>
        {loading ? (
          <span className={styles.muted}>Lecture de l'état...</span>
        ) : error || !status ? (
          <div className={styles.cardState}>
            <span className={`${styles.pill} ${styles.pillUnknown}`}>État inconnu</span>
            <span className={styles.muted} title={error ?? undefined}>
              Activation depuis le panel indisponible : le satellite est trop ancien (version 1.0.24 ou plus) ou la
              ModerationAPI ne répond pas. En jeu, /whitelistall marche toujours ; un nom ajouté ici ne compte qu'au
              prochain /whitelistall on ou redémarrage du proxy.
            </span>
            <button className={styles.smallButton} onClick={load}>Réessayer</button>
          </div>
        ) : !status.network ? (
          <span className={styles.muted}>
            Ce groupe n'a pas de liste blanche du réseau : chaque groupe de jeu a la sienne. Les listes des serveurs sont plus bas.
          </span>
        ) : (
          <>
            <div className={styles.cardState}>
              <span className={`${styles.pill} ${status.enabled ? styles.pillOn : styles.pillOff}`}>
                {status.enabled ? 'Activée' : 'Désactivée'}
              </span>
              <span className={styles.muted}>
                {status.enabled
                  ? `Seuls les joueurs inscrits (et ceux qui ont le contournement) entrent sur ${where}.`
                  : `Tout joueur peut entrer sur ${where}.`}
              </span>
              {!status.proxySync && (
                <span className={styles.notice}>
                  Les proxys ne peuvent pas être prévenus : {status.proxySyncError}
                </span>
              )}
            </div>
            {writable && (
              <button
                className={status.enabled ? styles.toggleOff : styles.toggleOn}
                disabled={saving || !status.proxySync}
                onClick={() => setConfirming(!status.enabled)}
              >
                {status.enabled ? 'Désactiver' : 'Activer'}
              </button>
            )}
          </>
        )}
      </div>

      {confirming !== null && (
        <div className={settings.modalOverlay} onClick={() => !saving && setConfirming(null)}>
          <div className={settings.modalSmall} onClick={(e) => e.stopPropagation()}>
            <h3 className={settings.deleteModalTitle}>
              {confirming ? 'Activer la liste blanche du réseau ?' : 'Désactiver la liste blanche du réseau ?'}
            </h3>
            <GameGroupBadge block caption="Groupe de jeu" />
            <p className={settings.deleteModalDescription}>
              {confirming
                ? group
                  ? <>Les joueurs non inscrits qui jouent sur un serveur de <strong>{group.label}</strong> seront renvoyés au lobby, sans être déconnectés. Ceux qui ont le contournement ou sont prime restent.</>
                  : <>Les joueurs non inscrits seront déconnectés du réseau. Ceux qui ont le contournement restent.</>
                : <>Tout joueur pourra de nouveau entrer sur {group ? <strong>{group.label}</strong> : 'le réseau'}.</>}
            </p>
            <div className={settings.modalActions}>
              <button className={settings.modalCancelButton} onClick={() => setConfirming(null)} disabled={saving}>
                Annuler
              </button>
              <button
                className={confirming ? settings.modalConfirmButtonDanger : settings.modalConfirmButton}
                onClick={() => apply(confirming)}
                disabled={saving}
              >
                {saving ? 'Envoi...' : confirming ? 'Activer' : 'Désactiver'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
